import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  addDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  DEFAULT_REVIEW_CONFIG,
  EVENTS_COLLECTION,
  FEEDBACK_COLLECTION,
  PUBLIC_CONFIG_COLLECTION,
  REVIEW_EVENT_TYPES,
} from './constants';
import { validateGoogleReviewUrl } from './validateGoogleReviewUrl';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { buildReviewPageUrl } from './buildReviewPageUrl';
import { sendReviewRequestEmail } from './reviewEmail';
import { aiConsentService } from '../../services/aiConsentService';
import { getCallableErrorMessage } from '../../utils/serviceErrorMessage';

function mergeReviewConfig(clinicData) {
  const stored = clinicData?.googleReviews;
  return {
    enabled: stored?.enabled === true,
    googleReviewUrl: String(stored?.googleReviewUrl || '').trim(),
  };
}

function pickPublicFields(clinicId, clinicData, config) {
  return {
    clinicId,
    enabled: config.enabled === true,
    clinicName: clinicData?.name || 'Dental practice',
    logoUrl: clinicData?.branding?.logo || '',
    primaryColor: clinicData?.branding?.primaryColor || '#2563EB',
    googleReviewUrl: config.googleReviewUrl,
    updatedAt: serverTimestamp(),
  };
}

export const reviewService = {
  getConfigFromClinic(clinicData) {
    return mergeReviewConfig(clinicData);
  },

  async loadClinicReviewBundle(clinicId) {
    const clinicRef = doc(db, 'clinics', clinicId);
    const clinicSnap = await getDoc(clinicRef);
    if (!clinicSnap.exists()) {
      throw new Error('Clinic not found.');
    }
    const clinicData = clinicSnap.data();
    const config = mergeReviewConfig(clinicData);
    return { clinicData, config };
  },

  async saveReviewConfig(clinicId, { enabled, googleReviewUrl }) {
    const { clinicData } = await this.loadClinicReviewBundle(clinicId);
    const next = {
      enabled: enabled === true,
      googleReviewUrl: String(googleReviewUrl || '').trim(),
    };

    if (next.enabled) {
      const check = validateGoogleReviewUrl(next.googleReviewUrl);
      if (!check.valid) {
        throw new Error(check.error);
      }
      next.googleReviewUrl = check.normalized;
    }

    await updateDoc(doc(db, 'clinics', clinicId), {
      googleReviews: next,
    });

    const publicPayload = pickPublicFields(clinicId, clinicData, next);
    await setDoc(doc(db, PUBLIC_CONFIG_COLLECTION, clinicId), publicPayload, { merge: true });

    return next;
  },

  async getPublicReviewConfig(clinicId) {
    const ref = doc(db, PUBLIC_CONFIG_COLLECTION, clinicId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    if (data.enabled !== true || !data.googleReviewUrl) return null;
    return {
      clinicId,
      clinicName: data.clinicName || 'Dental practice',
      logoUrl: data.logoUrl || '',
      primaryColor: data.primaryColor || '#2563EB',
      googleReviewUrl: data.googleReviewUrl,
    };
  },

  async submitPrivateFeedback({ clinicId, patientName, comment, patientEmail, consentId, outreachContactId }) {
    const body = String(comment || '').trim();
    if (!body) throw new Error('Please enter your feedback.');
    if (body.length > 2000) throw new Error('Feedback is too long (max 2000 characters).');

    const config = await this.getPublicReviewConfig(clinicId);
    if (!config) throw new Error('Reviews are not available for this practice.');

    const email = String(patientEmail || '').trim().toLowerCase().slice(0, 254);
    if (!email) {
      throw new Error('Please enter your email so the practice can respond.');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error('Please enter a valid email address.');
    }

    const linkedConsentId = String(consentId || '').trim().slice(0, 128);
    const linkedOutreachId = String(outreachContactId || '').trim().slice(0, 128);
    const fromReviewList = !!linkedOutreachId && !linkedConsentId;

    const payload = {
      clinicId,
      patientName: String(patientName || '').trim().slice(0, 120) || null,
      patientEmail: email,
      comment: body,
      read: false,
      source: fromReviewList ? 'review_list' : 'public_review_page',
      createdAt: serverTimestamp(),
      ...(linkedConsentId ? { consentId: linkedConsentId } : {}),
      ...(linkedOutreachId ? { outreachContactId: linkedOutreachId } : {}),
    };

    const ref = await addDoc(collection(db, FEEDBACK_COLLECTION), payload);

    await addDoc(collection(db, EVENTS_COLLECTION), {
      clinicId,
      type: REVIEW_EVENT_TYPES.FEEDBACK_SUBMITTED,
      feedbackId: ref.id,
      createdAt: serverTimestamp(),
    });

    return { id: ref.id };
  },

  async logGoogleClick(clinicId, options = {}) {
    const feedbackId = String(options.feedbackId || '').trim();
    if (feedbackId) {
      try {
        const fn = httpsCallable(functions, 'recordFeedbackReviewEngagement');
        await fn({ clinicId, feedbackId, type: 'google_clicked' });
        return;
      } catch {
        /* fall through to clinic-level event */
      }
    }
    const config = await this.getPublicReviewConfig(clinicId);
    if (!config) return;
    await addDoc(collection(db, EVENTS_COLLECTION), {
      clinicId,
      type: REVIEW_EVENT_TYPES.GOOGLE_CLICKED,
      ...(feedbackId ? { feedbackId } : {}),
      createdAt: serverTimestamp(),
    });
  },

  /** Patient opened the review page from a private-feedback Google ask email (?feedback=). */
  async recordFeedbackReviewLinkOpen({ clinicId, feedbackId }) {
    const cid = String(clinicId || '').trim();
    const fid = String(feedbackId || '').trim();
    if (!cid || !fid) return;
    const fn = httpsCallable(functions, 'recordFeedbackReviewEngagement');
    await fn({ clinicId: cid, feedbackId: fid, type: 'link_opened' });
  },

  async logLinkSent(clinicId, meta = {}) {
    await addDoc(collection(db, EVENTS_COLLECTION), {
      clinicId,
      type: REVIEW_EVENT_TYPES.LINK_SENT,
      patientEmail: meta.patientEmail || null,
      patientName: meta.patientName || null,
      createdAt: serverTimestamp(),
    });
  },

  async listFeedback(clinicId, max = 100) {
    const q = query(
      collection(db, FEEDBACK_COLLECTION),
      where('clinicId', '==', clinicId),
      orderBy('createdAt', 'desc'),
      limit(max)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /** Live private-feedback list (includes open-tracking fields after Google ask emails). */
  subscribeFeedback(clinicId, onItems, max = 40) {
    const id = String(clinicId || '').trim();
    if (!id || typeof onItems !== 'function') {
      return () => {};
    }
    const q = query(
      collection(db, FEEDBACK_COLLECTION),
      where('clinicId', '==', id),
      orderBy('createdAt', 'desc'),
      limit(max)
    );
    return onSnapshot(
      q,
      (snap) => onItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => onItems([])
    );
  },

  async markFeedbackRead(feedbackId) {
    await updateDoc(doc(db, FEEDBACK_COLLECTION, feedbackId), {
      read: true,
      readAt: serverTimestamp(),
    });
  },

  async deleteFeedback(feedbackId) {
    const id = String(feedbackId || '').trim();
    if (!id) throw new Error('Feedback ID is required.');
    await deleteDoc(doc(db, FEEDBACK_COLLECTION, id));
  },

  async countUnreadFeedback(clinicId) {
    const id = String(clinicId || '').trim();
    if (!id) return 0;
    const q = query(
      collection(db, FEEDBACK_COLLECTION),
      where('clinicId', '==', id),
      where('read', '==', false)
    );
    const snap = await getDocs(q);
    return snap.size;
  },

  /** Live unread private-feedback counts, split by Reviews vs Review list. */
  subscribeUnreadFeedbackCount(clinicId, onCount) {
    const id = String(clinicId || '').trim();
    if (!id || typeof onCount !== 'function') {
      return () => {};
    }
    const q = query(
      collection(db, FEEDBACK_COLLECTION),
      where('clinicId', '==', id),
      where('read', '==', false)
    );
    return onSnapshot(
      q,
      (snap) => {
        let reviews = 0;
        let reviewList = 0;
        snap.forEach((d) => {
          const data = d.data() || {};
          if (data.source === 'review_list' || data.outreachContactId) reviewList += 1;
          else reviews += 1;
        });
        onCount(reviews, { reviews, reviewList });
      },
      () => onCount(0, { reviews: 0, reviewList: 0 })
    );
  },

  async recordReviewRequestOnConsent(consentId, options = {}) {
    const {
      to = '',
      sentByName = null,
      sentByUserId = null,
      emailSource = null,
      subject = null,
      html = null,
      trackingId = null,
    } = options;
    const logEntry = {
      action: 'review_request',
      to: (to || '').trim(),
      at: Timestamp.now(),
      ...(sentByName ? { sentBy: sentByName } : {}),
      ...(sentByUserId ? { sentByUserId } : {}),
      ...(emailSource ? { emailSource } : {}),
      ...(subject ? { subject: String(subject).slice(0, 300) } : {}),
      ...(html ? { html: String(html).slice(0, 50000) } : {}),
      ...(trackingId ? { trackingId: String(trackingId) } : {}),
    };
    await updateDoc(doc(db, 'aiConsents', consentId), {
      'reviewRequest.lastSentAt': serverTimestamp(),
      'reviewRequest.lastSentTo': (to || '').trim(),
      'reviewRequest.sentCount': increment(1),
      ...(sentByUserId ? { 'reviewRequest.lastSentByUserId': sentByUserId } : {}),
      ...(sentByName ? { 'reviewRequest.lastSentByName': sentByName } : {}),
      emailDeliveryLog: arrayUnion(logEntry),
    });
  },

  /** Patient opened the review page from a consent-specific email link. */
  async recordReviewLinkOpen({ clinicId, consentId }) {
    const cid = String(clinicId || '').trim();
    const consent = String(consentId || '').trim();
    if (!cid || !consent) return null;
    const fn = httpsCallable(functions, 'recordReviewLinkOpen');
    const result = await fn({ clinicId: cid, consentId: consent });
    const data = result?.data || {};
    return {
      success: !!data.success,
      firstOpen: !!data.firstOpen,
      patientName: data.patientName ? String(data.patientName) : '',
      patientEmail: data.patientEmail ? String(data.patientEmail) : '',
    };
  },

  /**
   * Email a Google review link after positive private feedback.
   * Uses feedback.patientEmail, optional `to`, or email from linked consent.
   */
  async sendGoogleReviewAskFromFeedback({
    feedbackId,
    clinicId,
    clinicName,
    userData,
    to,
    patientName,
  }) {
    const { config } = await this.loadClinicReviewBundle(clinicId);
    if (!config.enabled || !config.googleReviewUrl) {
      throw new Error('Set up Google reviews first in Reviews (sidebar).');
    }

    const sendFn = httpsCallable(functions, 'sendGoogleReviewRequestFromFeedback');
    const sentByName = userData?.displayName || userData?.email?.split('@')[0] || 'Staff';
    try {
      const result = await sendFn({
        feedbackId,
        clinicId,
        clinicName: clinicName || 'Your dental practice',
        ...(to ? { to: String(to).trim() } : {}),
        ...(patientName ? { patientName: String(patientName).trim() } : {}),
        sentByName,
        sentByUserId: userData?.uid || null,
      });
      return result.data || { success: true };
    } catch (error) {
      throw new Error(
        getCallableErrorMessage(error) || 'Failed to send Google review request.'
      );
    }
  },

  /**
   * Send Google review request for a signed consent (patient email resolved automatically).
   */
  async sendReviewRequestForConsent({ consentId, consent, clinicId, clinicName, userData }) {
    const { config } = await this.loadClinicReviewBundle(clinicId);
    if (!config.enabled || !config.googleReviewUrl) {
      throw new Error('Set up Google reviews first in Reviews (sidebar) or Settings.');
    }

    const fullConsent = consent?.accessToken
      ? consent
      : await aiConsentService.getConsentById(consentId);

    if (String(fullConsent.status || '').toLowerCase() !== 'signed') {
      throw new Error('Review requests can only be sent for signed consents.');
    }

    const to = await aiConsentService.resolveDeliveryEmailForConsent(fullConsent);
    if (!to) throw new Error('No patient email on file for this consent.');

    const patientName = (fullConsent.patientName || 'Patient').trim();
    const clinic = clinicName || fullConsent.clinicName || 'Your dental practice';
    const resolvedConsentId = String(consentId || fullConsent.id || '').trim();
    if (!resolvedConsentId) {
      throw new Error('Consent ID is required.');
    }
    const reviewPageUrl = buildReviewPageUrl(clinicId, { consentId: resolvedConsentId });

    const storedEmail = (fullConsent.patientEmail || '').trim();
    const emailSource =
      fullConsent.patientId && storedEmail && storedEmail !== to
        ? 'patient_record'
        : fullConsent.patientId && to
          ? 'patient_record'
          : 'consent_record';

    const sentByName = userData?.displayName || userData?.email?.split('@')[0] || 'Staff';

    // Server records delivery log + lastSent* after SMTP accept (avoids duplicate writes / doc bloat).
    const sendResult = await sendReviewRequestEmail({
      to,
      patientName,
      clinicName: clinic,
      reviewPageUrl,
      clinicId,
      consentId: resolvedConsentId,
      sentByName,
      sentByUserId: userData?.uid || null,
      emailSource,
    });

    await this.logLinkSent(clinicId, {
      patientEmail: to,
      patientName,
      consentId: resolvedConsentId,
    });

    return {
      to,
      patientName,
      subject: sendResult?.subject || null,
      resend: !!sendResult?.resend,
    };
  },

  async getStats(clinicId) {
    const [sentSnap, clickSnap, feedbackSnap] = await Promise.all([
      getDocs(
        query(
          collection(db, EVENTS_COLLECTION),
          where('clinicId', '==', clinicId),
          where('type', '==', REVIEW_EVENT_TYPES.LINK_SENT)
        )
      ),
      getDocs(
        query(
          collection(db, EVENTS_COLLECTION),
          where('clinicId', '==', clinicId),
          where('type', '==', REVIEW_EVENT_TYPES.GOOGLE_CLICKED)
        )
      ),
      getDocs(
        query(
          collection(db, FEEDBACK_COLLECTION),
          where('clinicId', '==', clinicId),
          where('read', '==', false)
        )
      ),
    ]);

    return {
      linksSent: sentSnap.size,
      googleClicks: clickSnap.size,
      unreadFeedback: feedbackSnap.size,
    };
  },
};

export { DEFAULT_REVIEW_CONFIG };
